/**
 * UsernameModal - Set display name + profile picture for group chat.
 * Avatar is COMPLETELY SEPARATE from main profile picture.
 * NSFW filter applied on upload via AWS Rekognition.
 */

import { useState, useEffect, useRef } from 'react'
import { useGroupChat } from '../GroupChatContext'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

export function UsernameModal() {
  const { setUsername } = useGroupChat()

  const [step, setStep] = useState<'avatar' | 'username' | 'notifications'>('avatar')
  const [input, setInput] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [checking, setChecking] = useState(false)
  const [availability, setAvailability] = useState<{ available: boolean; reason: string } | null>(null)

  // Notification state
  const [smsEnabled, setSmsEnabled] = useState(false)
  const [smsPhone, setSmsPhone] = useState('')

  // Avatar state
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [avatarUploaded, setAvatarUploaded] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const checkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Avatar file selection
  const handleAvatarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setError('')

    // Validate
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      setError('Please upload a JPG, PNG, WEBP, or GIF image')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB')
      return
    }

    setAvatarFile(file)
    setAvatarPreview(URL.createObjectURL(file))
  }

  // Avatar upload with NSFW check
  const handleAvatarUpload = async () => {
    if (!avatarFile) return
    setAvatarUploading(true)
    setError('')

    try {
      const token = localStorage.getItem('auth_token')
      const formData = new FormData()
      formData.append('avatar', avatarFile)

      const res = await fetch(`${API_URL}/api/groupchat/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      })

      const data = await res.json()

      if (!res.ok) {
        setError(data.error || 'Upload failed')
        setAvatarUploading(false)
        return
      }

      setAvatarUploaded(true)
      setAvatarUploading(false)
      // Move to username step
      setStep('username')
    } catch {
      setError('Failed to upload image')
      setAvatarUploading(false)
    }
  }

  // Username availability check (debounced)
  useEffect(() => {
    if (input.length < 3) { setAvailability(null); return }
    if (!/^[a-z0-9_]+$/.test(input.toLowerCase())) {
      setAvailability({ available: false, reason: 'Only letters, numbers, and underscores' })
      return
    }

    if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current)

    checkTimeoutRef.current = setTimeout(async () => {
      setChecking(true)
      try {
        const token = localStorage.getItem('auth_token')
        const res = await fetch(
          `${API_URL}/api/groupchat/check-username/${encodeURIComponent(input.toLowerCase())}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        const data = await res.json()
        setAvailability({ available: data.available, reason: data.reason })
      } catch { setAvailability(null) }
      finally { setChecking(false) }
    }, 500)

    return () => { if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current) }
  }, [input])

  const handleSubmit = async () => {
    if (!input.trim() || loading) return
    if (availability && !availability.available) return
    setStep('notifications')
  }

  const handleFinish = async () => {
    setLoading(true)
    setError('')

    // Save notification preferences first (non-blocking)
    if (smsEnabled && smsPhone.trim()) {
      const token = localStorage.getItem('auth_token')
      fetch(`${API_URL}/api/groupchat/notifications`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ smsEnabled: true, smsPhone: smsPhone.trim() })
      }).catch(() => {})
    }

    // Set username + connect
    const result = await setUsername(input.toLowerCase())
    if (!result.success) {
      setError(result.error || 'Failed to set username')
      setLoading(false)
    }
  }

  const canSubmit = input.length >= 3 && input.length <= 20 && !loading && !checking && availability?.available === true

  return (
    <div className="min-h-screen bg-pink-50 flex items-center justify-center p-4">
      <div className="bg-white border border-pink-200 rounded-2xl p-6 sm:p-8 w-full max-w-md shadow-lg">

        {/* Step 1: Avatar */}
        {step === 'avatar' && (
          <>
            <div className="text-center mb-6">
              <div className="text-4xl mb-3">📸</div>
              <h2 className="text-gray-900 text-xl font-bold mb-2">Set Your Chat Photo</h2>
              <p className="text-gray-500 text-sm">
                This photo is only for group chat — separate from your main profile.
              </p>
            </div>

            <div className="space-y-4">
              {/* Preview circle */}
              <div className="flex justify-center">
                <div
                  className="w-28 h-28 rounded-full border-2 border-pink-300 flex items-center justify-center overflow-hidden cursor-pointer hover:border-pink-500 transition-colors bg-pink-50"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center">
                      <svg className="w-8 h-8 text-pink-300 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span className="text-xs text-pink-400">Add photo</span>
                    </div>
                  )}
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleAvatarSelect}
                className="hidden"
              />

              <p className="text-center text-xs text-gray-400">JPG, PNG, WEBP, or GIF — max 5MB</p>

              {error && <p className="text-red-500 text-sm text-center">{error}</p>}

              <div className="flex gap-3">
                <button
                  onClick={() => setStep('username')}
                  className="flex-1 py-3 rounded-xl text-gray-500 font-medium text-sm hover:bg-gray-50 transition-colors"
                >
                  Skip for now
                </button>
                <button
                  onClick={handleAvatarUpload}
                  disabled={!avatarFile || avatarUploading}
                  className="flex-1 py-3 rounded-xl text-white font-semibold text-sm transition-all disabled:opacity-40"
                  style={{ backgroundColor: '#ec4899' }}
                >
                  {avatarUploading ? 'Checking...' : 'Upload'}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Step 2: Username */}
        {step === 'username' && (
          <>
            <div className="text-center mb-6">
              {avatarUploaded && avatarPreview && (
                <div className="w-16 h-16 rounded-full overflow-hidden mx-auto mb-3 border-2 border-pink-300">
                  <img src={avatarPreview} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              {!avatarUploaded && <div className="text-4xl mb-3">💬</div>}
              <h2 className="text-gray-900 text-xl font-bold mb-2">Choose Your Username</h2>
              <p className="text-gray-500 text-sm">This is how others will see you in chat.</p>
            </div>

            <div className="space-y-4">
              <div>
                <input
                  type="text"
                  value={input}
                  onChange={(e) => { setInput(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')); setError('') }}
                  placeholder="username"
                  maxLength={20}
                  className={`w-full bg-pink-50 border rounded-xl px-4 py-3 text-gray-800 text-base placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                    availability === null
                      ? 'border-pink-200 focus:ring-pink-200 focus:border-pink-400'
                      : availability.available
                        ? 'border-green-400 focus:ring-green-200 focus:border-green-500'
                        : 'border-red-400 focus:ring-red-200 focus:border-red-500'
                  }`}
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) handleSubmit() }}
                />
                <div className="flex justify-between mt-1.5">
                  <span className="text-xs text-gray-400">{input.length}/20</span>
                  {checking && <span className="text-xs text-pink-400">Checking...</span>}
                  {!checking && availability && (
                    <span className={`text-xs ${availability.available ? 'text-green-600' : 'text-red-500'}`}>
                      {availability.reason}
                    </span>
                  )}
                </div>
              </div>

              <div className="text-xs text-gray-400 space-y-1">
                <p className={input.length >= 3 ? 'text-green-600' : ''}>- 3-20 characters</p>
                <p className={input.length > 0 && /^[a-z0-9_]+$/.test(input) ? 'text-green-600' : ''}>
                  - Letters, numbers, and underscores only
                </p>
              </div>

              {error && <p className="text-red-500 text-sm text-center">{error}</p>}

              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="w-full py-3 rounded-xl text-white font-semibold transition-all disabled:opacity-40 hover:opacity-90 active:scale-[0.98]"
                style={{ backgroundColor: '#ec4899' }}
              >
                Continue
              </button>
            </div>
          </>
        )}

        {/* Step 3: Notifications */}
        {step === 'notifications' && (
          <>
            <div className="text-center mb-6">
              <div className="text-4xl mb-3">🔔</div>
              <h2 className="text-gray-900 text-xl font-bold mb-2">Stay Updated</h2>
              <p className="text-gray-500 text-sm">
                Get notified about important messages in group chat.
              </p>
            </div>

            <div className="space-y-4">
              {/* SMS Toggle */}
              <div className="flex items-center justify-between p-3 bg-pink-50 rounded-xl border border-pink-200">
                <div>
                  <p className="text-sm font-medium text-gray-900">SMS Notifications</p>
                  <p className="text-xs text-gray-400">Get texts for important updates</p>
                </div>
                <button
                  onClick={() => setSmsEnabled(!smsEnabled)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${smsEnabled ? 'bg-pink-500' : 'bg-gray-300'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${smsEnabled ? 'translate-x-5' : ''}`} />
                </button>
              </div>

              {smsEnabled && (
                <input
                  type="tel"
                  value={smsPhone}
                  onChange={e => setSmsPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="w-full bg-pink-50 border border-pink-200 rounded-xl px-4 py-3 text-base text-gray-800 placeholder-gray-400 focus:outline-none focus:border-pink-400"
                />
              )}

              {error && <p className="text-red-500 text-sm text-center">{error}</p>}

              <div className="flex gap-3">
                <button
                  onClick={handleFinish}
                  disabled={loading}
                  className="flex-1 py-3 rounded-xl text-gray-500 text-sm font-medium hover:bg-gray-50"
                >
                  {loading ? 'Joining...' : 'Skip'}
                </button>
                <button
                  onClick={handleFinish}
                  disabled={loading || (smsEnabled && !smsPhone.trim())}
                  className="flex-1 py-3 rounded-xl text-white font-semibold text-sm transition-all disabled:opacity-40"
                  style={{ backgroundColor: '#ec4899' }}
                >
                  {loading ? 'Joining...' : 'Join Chat'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
